import React, {useEffect, useMemo, useRef, useState} from "react";
import {
    Avatar,
    Badge,
    Button,
    Card,
    Dropdown,
    Grid,
    Input,
    List,
    Modal,
    Space,
    Tag,
    Tooltip,
    Typography,
    Upload,
    message,
    Select,
    theme as antdTheme,
} from "antd";
import {
    DownloadOutlined,
    InfoCircleOutlined,
    MenuOutlined,
    PaperClipOutlined,
    RobotOutlined,
    SendOutlined,
    UserOutlined,
    FilePdfOutlined,
    FileWordOutlined,
    FileExcelOutlined,
    FilePptOutlined,
    FileImageOutlined,
    AudioOutlined,
    VideoCameraOutlined,
    FileTextOutlined,
    LinkOutlined,
    DesktopOutlined,
    ArrowRightOutlined,
    CustomerServiceOutlined,
    EditOutlined,
    MoreOutlined,
    SwapOutlined,
} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import {useNavigate, useParams, useLocation} from "react-router-dom";
import DateObject from "react-date-object";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import {useSelfProfile} from "../header-query";
import {useStompClient, useSubscription} from "react-stomp-hooks";
import {
    useMarkChatAsRead,
    useCloseChat,
    useCreateMessage,
    useOperatorsAfterTime,
    useTransferChat,
} from "./chat-query";
import {editMessageText, uploadFileToMinio, fetchMessagesByChatId, fetchMyChatList} from "./chat-api";
import {isFinishedState, stateMeta} from "../../../utils/chatState";
import VisitorJourney from "./visitor-journey";
import {SESSION_ENDED_EVENT, isSessionEnded} from "../../../config/token-refresh";
import {playNotificationSound} from "../../../config/notification-sound";
import {onReturnToTab} from "../../../config/visibility";
import {channelMeta as sharedChannelMeta} from "../../../utils/channel";

const {Text, Title} = Typography;
const {TextArea} = Input;
const {useBreakpoint} = Grid;

/* ---------- helpers ---------- */
const toPersianDateObject = (d: any) =>
    d ? new DateObject({date: new Date(d), calendar: persian, locale: persian_fa}) : null;

const fmtPersian = (d: any, token = "YYYY/MM/DD HH:mm:ss") => {
    const obj = toPersianDateObject(d);
    return obj ? obj.format(token) : "-";
};

const faDateWithHour = (d: any) => fmtPersian(d, "HH:mm YYYY/MM/DD");

// `name = ""` only covers undefined; a NULL from the server (a customer the CRM never knew) reached
// .trim() and took the whole console down to a white page. Coerce first.
const getInitials = (name) => {
    const parts = String(name ?? "").trim().split(/\s+/);
    return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase();
};

const faDigits = (v: any) =>
    String(v ?? "").replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[Number(d)]);

/* ---------- سن نسبی ردیف صف ---------- */
/** پس از این مدت، یک گفت‌وگوی در صف پررنگ و قرمز نمایش داده می‌شود. */
const WAIT_ALERT_MS = 5 * 60 * 1000;

/**
 * How long this row has been sitting. Computed from updatedAt on purpose: ChatDTO.waitingTime
 * is only filled at the WAITING -> OPERATOR transition, so it is null for exactly the rows that
 * are still queued — and updatedAt is the same anchor the backend uses for the wait.
 */
const relAge = (date: any, now: number) => {
    if (!date) return "-";
    const ms = now - new Date(date).getTime();
    if (!isFinite(ms)) return "-";
    const min = Math.floor(Math.max(ms, 0) / 60000);
    if (min < 1) return "همین الان";
    if (min < 60) return `${faDigits(min)} دقیقه`;
    const hours = Math.floor(min / 60);
    if (hours < 24) return `${faDigits(hours)} ساعت`;
    return `${faDigits(Math.floor(hours / 24))} روز`;
};

/* ---------- تایپینگ زنده ---------- */
/**
 * حداقل فاصلهٔ بین دو ارسالِ «اپراتور در حال تایپ است»؛ بدون آن هر کلید یک فریم روی سوکت می‌شد.
 */
const TYPING_PUBLISH_THROTTLE_MS = 350;
/**
 * پیش‌نویس بازدیدکننده پس از این مدت سکوت از صفحه پاک می‌شود. اگر سوکت بیفتد، فریم پایانیِ
 * typing=false هرگز نمی‌رسد و بدون این مهلت یک متنِ کهنه برای همیشه زیر ترنسکریپت می‌ماند.
 */
const VISITOR_DRAFT_TTL_MS = 6000;

/* ---------- نقش‌ها برای بابل ---------- */
/**
 * برچسب حباب. برچسبِ اپراتور از بیرون تزریق می‌شود و ثابت نیست: «شما (اپراتور)» فقط وقتی
 * درست است که گفت‌وگو به همین اپراتورِ واردشده تعلق داشته باشد. برای گفت‌وگویی که به اپراتور
 * دیگری تعلق دارد (مثلاً از تاریخچه باز شده یا پیش از انتقال دست همکار بوده) باید نام همان
 * اپراتور دیده شود، وگرنه خواننده قول‌های همکارش را قول خودش می‌خواند.
 */
const roleMeta = (sender: string, operatorLabel: string = "اپراتور") => {
    if (sender === "USER") {
        return {
            label: "کاربر",
            isUser: true,
            bg: "rgba(244,143,177,0.12)",
            border: "1px solid rgba(244,143,177,0.5)",
            avatarBg: "#f06292",
            align: "flex-end",
        };
    }
    if (sender === "AGENT") {
        return {
            label: "ایجنت هوش مصنوعی",
            isUser: false,
            bg: "rgba(159,168,218,0.14)",
            border: "1px solid rgba(159,168,218,0.5)",
            avatarBg: "#5c6bc0",
            align: "flex-start",
        };
    }
    return {
        label: operatorLabel,
        isUser: false,
        bg: "rgba(129,199,132,0.12)",
        border: "1px solid rgba(129,199,132,0.45)",
        avatarBg: "#43a047",
        align: "flex-start",
    };
};

/* ---------- کانال و زبان گفت‌وگو ---------- */
/**
 * Channel presentation. The backend never sends NULL (a legacy Telegram row is reported as
 * "TELEGRAM"), but default here too so the console can't render a blank badge.
 */
// Presentation comes from utils/channel.js so the live console and chat history cannot drift apart
// — they had already drifted, calling the same web chat «وب‌سایت» here and «چت آنلاین سایت» there.
const channelMeta = (channel) => sharedChannelMeta(channel);

const LANG_LABEL = {fa: "فارسی", en: "English", ar: "العربية"};

/**
 * The language the customer chose — the Telegram bot's picker or the widget's language — so the
 * operator knows which language to answer in. NULL for conversations from before either existed;
 * those are Persian, and saying so would be a guess.
 */
const langLabel = (lang) => (lang ? LANG_LABEL[lang] || lang : null);

/** Only the languages that are NOT the desk's own get colour, so they stand out in a long list. */
const foreignLangLabel = (lang) => (lang && lang !== "fa" ? LANG_LABEL[lang] || lang : null);

/** Coarse device summary from a user-agent; good enough to tell mobile from desktop at a glance. */
const deviceFromUA = (ua) => {
    if (!ua) return null;
    const s = ua.toLowerCase();
    const os = /iphone|ipad|ipod/.test(s) ? "iOS"
        : /android/.test(s) ? "Android"
        : /windows/.test(s) ? "Windows"
        : /mac os/.test(s) ? "macOS"
        : /linux/.test(s) ? "Linux" : null;
    const br = /edg\//.test(s) ? "Edge"
        : /chrome\//.test(s) && !/edg\//.test(s) ? "Chrome"
        : /firefox\//.test(s) ? "Firefox"
        : /safari\//.test(s) && !/chrome\//.test(s) ? "Safari" : null;
    const kind = /mobile|iphone|android/.test(s) ? "موبایل" : "دسکتاپ";
    return [kind, os, br].filter(Boolean).join(" · ");
};

/* ---------- فایل‌ها به استایل کارت ---------- */
// Voice notes (the customer's, and the AI's spoken answers) play right in the transcript instead of
// opening a new tab; screenshots and photos show as a preview. webm/oga/opus are what browsers and
// Telegram record voice in.
const AUDIO_EXTS = ["mp3", "wav", "ogg", "oga", "opus", "m4a", "webm"];
const IMAGE_EXTS = ["png", "jpg", "jpeg", "gif", "webp"];

const getFileMeta = (ext: string) => {
    ext = ext.toLowerCase();
    if (ext === "pdf")
        return {icon: <FilePdfOutlined/>, iconBg: "#e53935"};
    if (["doc", "docx"].includes(ext))
        return {icon: <FileWordOutlined/>, iconBg: "#1e88e5"};
    if (["xls", "xlsx"].includes(ext))
        return {icon: <FileExcelOutlined/>, iconBg: "#43a047"};
    if (["ppt", "pptx"].includes(ext))
        return {icon: <FilePptOutlined/>, iconBg: "#fb8c00"};
    if (["png", "jpg", "jpeg", "gif", "webp"].includes(ext))
        return {icon: <FileImageOutlined/>, iconBg: "#8e24aa"};
    if (AUDIO_EXTS.includes(ext))
        return {icon: <AudioOutlined/>, iconBg: "#00acc1"};
    if (["mp4", "mkv", "mov", "avi"].includes(ext))
        return {icon: <VideoCameraOutlined/>, iconBg: "#00897b"};
    return {icon: <FileTextOutlined/>, iconBg: "#757575"};
};

/** حباب فایل شبیه عکس که فرستادی */
const renderAttachments = (files: any[] = [], isUser = false) => {
    if (!files || !files.length) return null;

    return (
        <div style={{marginTop: 8, display: "flex", flexDirection: "column", gap: 6}}>
            {files.map((f) => {
                const ext = (f.fileExtension || "").toLowerCase();
                const {icon, iconBg} = getFileMeta(ext);

                const openFile = (e: React.MouseEvent) => {
                    e.stopPropagation();
                    if (f.downloadUrl && f.downloadUrl !== "#") {
                        window.open(f.downloadUrl, "_blank");
                    }
                };
                const hasUrl = f.downloadUrl && f.downloadUrl !== "#";

                if (hasUrl && AUDIO_EXTS.includes(ext)) {
                    return (
                        <div key={f.filePath + f.fileName}
                             style={{display: "flex", alignItems: "center", gap: 4,
                                 justifyContent: isUser ? "flex-end" : "flex-start"}}>
                            {/* preload="none": a long transcript must not fetch every voice note up front. */}
                            <audio controls preload="none" src={f.downloadUrl}
                                   style={{height: 36, maxWidth: 260, minWidth: 200}}/>
                            <Button type="text" shape="circle" size="small" icon={<DownloadOutlined/>}
                                    onClick={openFile} aria-label="دانلود"/>
                        </div>
                    );
                }

                if (hasUrl && IMAGE_EXTS.includes(ext)) {
                    return (
                        <div key={f.filePath + f.fileName}
                             style={{display: "flex", justifyContent: isUser ? "flex-end" : "flex-start"}}>
                            <img src={f.downloadUrl} alt={f.fileName} loading="lazy" onClick={openFile}
                                 style={{maxWidth: 240, maxHeight: 240, borderRadius: 10, cursor: "zoom-in",
                                     objectFit: "cover", border: "1px solid #eee", display: "block"}}/>
                        </div>
                    );
                }

                return (
                    <div
                        key={f.filePath + f.fileName}
                        style={{
                            display: "flex",
                            justifyContent: isUser ? "flex-end" : "flex-start",
                        }}
                    >
                        <div
                            onClick={openFile}
                            style={{
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                padding: "6px 10px",
                                borderRadius: 999,
                                background: "#fde7f2",
                                border: "1px solid #f8bbd0",
                                maxWidth: 260,
                                minWidth: 180,
                                direction: "rtl",
                            }}
                        >
                            {/* آیکون فایل */}
                            <div
                                style={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: "50%",
                                    background: iconBg,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "#fff",
                                    flexShrink: 0,
                                }}
                            >
                                {icon}
                            </div>

                            {/* نام فایل */}
                            <span
                                style={{
                                    fontSize: 13,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    flex: 1,
                                }}
                            >
                                {f.fileName}
                            </span>

                            {/* دکمه دانلود */}
                            <Button
                                type="text"
                                shape="circle"
                                icon={<DownloadOutlined/>}
                                size="small"
                                onClick={openFile}
                            />
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

/* ---------- فرمت تایمر ویس ---------- */
const formatRecordTime = (ms: number) => {
    if (!ms || ms < 0) return "00.0";
    const totalSeconds = Math.floor(ms / 1000);

    // تا قبل از یک دقیقه: ثانیه و دهم ثانیه (ss.t)
    if (totalSeconds < 60) {
        const seconds = String(totalSeconds).padStart(2, "0");
        const tenths = Math.floor((ms % 1000) / 100);
        return `${seconds}.${tenths}`;
    }

    // بعد از یک دقیقه: دقیقه و ثانیه (mm:ss)
    const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, "0");
    const seconds = String(totalSeconds % 60).padStart(2, "0");
    return `${minutes}:${seconds}`;
};

/* ==================== MAIN COMPONENT ==================== */

// How long to wait for the socket's chat-list reply before falling back to HTTP. Long enough that a
// healthy connection always wins the race and the fallback stays invisible; short enough that a lost
// frame costs the operator a moment rather than a page reload.
const CHAT_LIST_FALLBACK_MS = 2500;

export default function ChatPage() {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {chatId} = useParams();
    const screens = useBreakpoint();
    // Theme tokens instead of hardcoded #fff / #f0f0f0, so the operator's main working
    // surface follows light/dark mode instead of staying white.
    const {token: themeToken} = antdTheme.useToken();
    const SURFACE = themeToken.colorBgContainer;
    const HAIRLINE = themeToken.colorBorderSecondary;
    const isDesktop = screens.md;
    const CHAT_HEIGHT = "77vh";
    const location = useLocation();
    const preloadedChat = location.state?.chat || null;

    const [chatList, setChatList] = useState([]);
    const [selectedChatId, setSelectedChatId] = useState(null);
    const [messagesByChat, setMessagesByChat] = useState({});
    const [messageText, setMessageText] = useState("");
    const [fileList, setFileList] = useState([]);
    const [uploadingCount, setUploadingCount] = useState(0);
    const [sending, setSending] = useState(false);
    const [showInfoModal, setShowInfoModal] = useState(false);

    // Inline message editing. Only one message is editable at a time — a transcript with several
    // half-finished edits open in it is a good way to save the wrong one.
    const [editingId, setEditingId] = useState(null);
    const [editText, setEditText] = useState("");
    const [editSaving, setEditSaving] = useState(false);

    // isSessionEnded() reads plain module state in token-refresh.js, so calling it during render
    // never re-renders when it flips. Without this the badge kept saying "connecting" over a session
    // already known to be dead — the exact lie this whole change set exists to stop telling.
    const [sessionDead, setSessionDead] = useState(() => isSessionEnded());
    useEffect(() => {
        const onEnded = () => setSessionDead(true);
        window.addEventListener(SESSION_ENDED_EVENT, onEnded);
        return () => window.removeEventListener(SESSION_ENDED_EVENT, onEnded);
    }, []);
    // تیک ۳۰ثانیه‌ای فقط برای تازه نگه‌داشتن «سن» ردیف‌های صف
    const [nowTs, setNowTs] = useState(() => Date.now());
    // تا اولین لیستِ رسیده از STOMP، نباید «پایان گفت‌وگو» نشان بدهیم
    const [chatListLoaded, setChatListLoaded] = useState(false);
    const selectedChatIdRef = useRef(null);
    // آیا سوکت از زمان آخرین درخواست، لیست را پاسخ داده است؟ مبنای تصمیمِ fallback روی HTTP.
    const chatListAnsweredRef = useRef(false);
    // همان پرسش، ولی به تفکیکِ هر گفت‌وگو: آیا ترنسکریپتِ این چت را سوکت پاسخ داده؟
    // «کشِ خالی نیست» دیگر معیار درستی نیست، چون کشِ پیام‌ها حالا انباشته می‌شود.
    const messageAnsweredRef = useRef({});

    /**
     * پیش‌نویسِ زندهٔ بازدیدکننده — چیزی که کاربر همین حالا دارد می‌نویسد و هنوز نفرستاده.
     * فقط در state زندگی می‌کند: نه در ترنسکریپت می‌رود، نه ذخیره می‌شود، نه به جایی برمی‌گردد.
     */
    const [visitorDraft, setVisitorDraft] = useState("");
    const visitorDraftTimerRef = useRef(null);
    /** وضعیت throttle برای اعلام «اپراتور در حال تایپ است» به بازدیدکننده. */
    const operatorTypingRef = useRef({last: 0, timer: null});

    // ویس
    const [isRecording, setIsRecording] = useState(false);
    const [recordElapsed, setRecordElapsed] = useState(0);
    const mediaRecorderRef = useRef(null);
    const recordTimerRef = useRef(null);

    const [transferModalOpen, setTransferModalOpen] = useState(false);
    const [transferTargetOperatorId, setTransferTargetOperatorId] = useState(null);
    const [transferOperators, setTransferOperators] = useState([]);

    const {data: me} = useSelfProfile()
    const stompClient = useStompClient();
    const {mutate: markChatAsRead} = useMarkChatAsRead();
    const {mutate: closeChat} = useCloseChat();
    const {mutateAsync: createMessage} = useCreateMessage();
    const {mutate: fetchOperatorsAfterTime, isPending: loadingTransferOperators} = useOperatorsAfterTime();

    const {mutate: transferChat, isPending: transferringChat} = useTransferChat(() => {
        message.success("چت با موفقیت به اپراتور جدید منتقل شد. ✅");
        setTransferModalOpen(false);
        setTransferTargetOperatorId(null);
    });

    const messagesContainerRef = useRef(null);

    /**
     * Audible cue for an incoming customer message in the OPEN chat. The bell in the shell only
     * fires on the notification feed, which does not cover a chat the operator is already looking
     * at — so a customer replying to an open conversation arrived silently.
     *
     * -1 means "not measured yet" so the initial transcript load never rings; reset on chat switch.
     */
    const inboundSeenRef = useRef(-1);

    useEffect(() => {
        inboundSeenRef.current = -1; // new conversation → re-baseline, don't ring on its history
    }, [selectedChatId]);

    // Same sound and volume as the bell — one setting, in the operator's own account page. The
    // hardcoded 0.5 that used to live here was the volume nobody could turn down.
    const playIncomingChime = () => playNotificationSound();

    /**
     * پیش‌نویس بازدیدکننده در چهار حالت پاک می‌شود: توقفِ تایپ (typing=false)، رسیدن پیام واقعی،
     * تعویض گفت‌وگو، و سکوتِ طولانی‌تر از VISITOR_DRAFT_TTL_MS.
     */
    const clearVisitorDraft = () => {
        if (visitorDraftTimerRef.current) {
            window.clearTimeout(visitorDraftTimerRef.current);
            visitorDraftTimerRef.current = null;
        }
        setVisitorDraft("");
    };

    /** مهلتِ زنده‌بودنِ پیش‌نویس را از نو کوک می‌کند؛ هر فریم تازه، مهلت را جلو می‌برد. */
    const armVisitorDraftExpiry = () => {
        if (visitorDraftTimerRef.current) window.clearTimeout(visitorDraftTimerRef.current);
        visitorDraftTimerRef.current = window.setTimeout(() => {
            visitorDraftTimerRef.current = null;
            setVisitorDraft("");
        }, VISITOR_DRAFT_TTL_MS);
    };

    // تعویض گفت‌وگو (و unmount): پیش‌نویسِ گفت‌وگوی قبلی و تایمرهای تایپ نباید باقی بمانند.
    useEffect(() => {
        const stopTypingTimers = () => {
            if (visitorDraftTimerRef.current) {
                window.clearTimeout(visitorDraftTimerRef.current);
                visitorDraftTimerRef.current = null;
            }
            if (operatorTypingRef.current.timer) {
                window.clearTimeout(operatorTypingRef.current.timer);
                operatorTypingRef.current.timer = null;
            }
        };
        stopTypingTimers();
        operatorTypingRef.current.last = 0;
        setVisitorDraft("");
        return stopTypingTimers;
    }, [selectedChatId]);

    const isUploadingFiles = uploadingCount > 0;

    useEffect(() => {
        selectedChatIdRef.current = selectedChatId;
    }, [selectedChatId]);

    useEffect(() => {
        const timer = window.setInterval(() => setNowTs(Date.now()), 30000);
        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        if (!selectedChatId) return;
        const current = chatList.find((c: any) => c.id === selectedChatId);
        if (!current) return;

        if (current.unread) {
            markChatAsRead(selectedChatId);
            setChatList((prev: any[]) =>
                prev.map((c: any) =>
                    c.id === selectedChatId ? {...c, unread: false} : c
                )
            );
        }
    }, [selectedChatId, chatList, markChatAsRead]);

    // SUBSCRIBE first, then request. react-stomp-hooks runs each useSubscription's internal subscribe
    // effect in declaration order, BEFORE the publish effects below — so on the single ordered WebSocket
    // the server always sees our SUBSCRIBE frames before the /app/... SEND requests. Previously the
    // request went out first and the server could answer /app/message/list before the per-chat queue
    // subscription existed, dropping the initial transcript (chat opened blank until re-clicked).

    // Per-user queue: Spring routes it to this session only, so no operatorId is in the
    // destination and one operator can never subscribe to another's chat list.
    const chatTopic = "/user/queue/chat/list";

    useSubscription(chatTopic, (messageStomp) => {
        try {
            const payload = JSON.parse(messageStomp.body); // List<ChatDTO>
            setChatList(payload);
            setChatListLoaded(true);
            chatListAnsweredRef.current = true;
        } catch (e) {
            console.error("Error parsing chat list payload", e);
        }
    });

    // Per-user queue for the open conversation. The chatId suffix lets us subscribe to just the
    // selected chat; the backend only delivers it after checking this operator owns that chat.
    // An EMPTY ARRAY when nothing is selected — not null.
    //
    // useSubscription wraps a non-array argument as [value], so passing null made it subscribe to the
    // literal destination "null". The server refuses that, and a refused SUBSCRIBE tears down the whole
    // STOMP session — so an operator sitting on the console with no chat open was knocked offline,
    // reconnected, subscribed to "null" again, and was knocked offline again, every three seconds,
    // forever. That is the "آفلاین — در حال اتصال…" that never resolves. An empty array subscribes to
    // nothing at all, while keeping this hook in the same declaration position (see the ordering note
    // above, which must not be broken).
    const messageTopic = selectedChatId ? [`/user/queue/message/list.${selectedChatId}`] : [];

    useSubscription(messageTopic, (messageStomp) => {
        try {
            const payload = JSON.parse(messageStomp.body);
            // Audible cue when the CUSTOMER writes into the chat we have open. Counted rather than
            // compared by id because the payload is a full transcript replace; the ref starts at -1
            // so the initial load (which replays the whole history) never rings.
            const inbound = (payload || []).filter((m) => m?.sender === "USER").length;
            const known = inboundSeenRef.current;
            if (known >= 0 && inbound > known) playIncomingChime();
            inboundSeenRef.current = inbound;

            // Keep the other conversations' cached transcripts. This used to discard `prev`
            // entirely, so every push for the open chat emptied every other chat's cache and
            // switching back to one of them re-rendered blank until its refetch landed.
            messageAnsweredRef.current[selectedChatId] = true;
            setMessagesByChat((prev) => ({
                ...prev,
                [selectedChatId]: payload,
            }));
            // پیام واقعی رسید؛ هر پیش‌نویسی که زیر ترنسکریپت مانده دیگر بی‌معناست.
            clearVisitorDraft();
        } catch (e) {
            console.error("Error parsing message list", e);
        }
    });

    // پیش‌نویس زندهٔ بازدیدکننده. عمداً کنارِ بقیهٔ useSubscription ها و *بالای* افکت‌های publish
    // اعلام شده تا همان ترتیبِ SUBSCRIBE-قبل-از-SEND بالا حفظ شود.
    // هیچ‌چیز از این صف ذخیره نمی‌شود: خوانده، نشان داده و فراموش می‌شود.
    // Empty array, for exactly the reason spelled out on messageTopic above.
    const visitorTypingTopic = selectedChatId
        ? [`/user/queue/chat/typing.${selectedChatId}`]
        : [];

    useSubscription(visitorTypingTopic, (messageStomp) => {
        try {
            const payload = JSON.parse(messageStomp.body); // {chatId, text, typing}
            // حصار: فریمِ درراهِ گفت‌وگوی قبلی نباید روی گفت‌وگوی تازه‌باز نشان داده شود.
            if (payload?.chatId && String(payload.chatId) !== String(selectedChatId)) return;

            const draft = typeof payload?.text === "string" ? payload.text : "";
            if (payload?.typing === false || draft.trim() === "") {
                clearVisitorDraft();
                return;
            }
            setVisitorDraft(draft);
            armVisitorDraftExpiry();
        } catch (e) {
            console.error("Error parsing visitor typing payload", e);
        }
    });

    useEffect(() => {
        if (!stompClient) return;
        try {
            // Backend derives the operator from the authenticated STOMP session (body ignored).
            // Re-runs on (re)connect because stompClient changes identity, so the list reloads
            // after a dropped connection.
            stompClient.publish({destination: "/app/chat/list", body: ""});
        } catch (e) {
            console.error("Error publishing /chat/list", e);
        }
    }, [stompClient]);

    /**
     * HTTP safety net for the inbox — the same one the transcript has had since it hit this bug.
     *
     * The list above arrives as ONE unacknowledged STOMP frame, and `chatList` starts empty on
     * every mount. Nothing else can ever add a row: the other setChatList calls all map over the
     * existing array, so an empty list is an absorbing state. Miss that single frame — for any
     * reason — and the operator sits in front of an empty inbox with no error, no spinner and no
     * retry, which is why the only known cure was F5. The bell kept working throughout because it
     * lives on a subscription that is established once at login and never remounts, which is
     * exactly the "it rings but the chat screen is empty" split that was reported.
     *
     * Two passes, deliberately. The first paints immediately so navigation feels instant even
     * before the socket answers. The second runs only if no push has arrived by then and replaces
     * whatever is on screen — that one is the actual recovery, and it is cause-agnostic: it does
     * not care WHY the frame was lost, only that it never came. Re-runs on every (re)connect.
     */
    useEffect(() => {
        let cancelled = false;
        chatListAnsweredRef.current = false;

        const loadOverRest = (replaceStale) => {
            // Checked before the request, not after: on a healthy connection the socket has already
            // answered by the time the watchdog fires, and a round-trip whose result we would throw
            // away is pure waste on every navigation.
            if (cancelled || chatListAnsweredRef.current) return;
            fetchMyChatList()
                .then((res) => {
                    // The socket won the race — its payload is authoritative, leave it alone.
                    if (cancelled || chatListAnsweredRef.current) return;
                    const rows = Array.isArray(res) ? res : (Array.isArray(res?.data) ? res.data : null);
                    // An EMPTY result must not mark the list "loaded". This endpoint now answers an
                    // ADMIN or SUPERVISOR with the whole desk rather than the [] an operator-less
                    // account used to get, but empty is still reachable — and claiming the list had
                    // loaded made the screen decide the conversation was over and render
                    // «این گفت‌وگو پایان یافت» instead of the transcript when an admin opened a chat
                    // from history. Only the socket can assert "you genuinely have no chats".
                    if (!rows || !rows.length) return;
                    setChatList((prev) => (replaceStale || !prev.length ? rows : prev));
                    setChatListLoaded(true);
                })
                .catch(() => {
                    /* the socket may still deliver; a failed prime must not surface as an error */
                });
        };

        loadOverRest(false);
        const watchdog = window.setTimeout(() => loadOverRest(true), CHAT_LIST_FALLBACK_MS);

        return () => {
            cancelled = true;
            clearTimeout(watchdog);
        };
    }, [stompClient]);

    useEffect(() => {
        if (!stompClient || !selectedChatId) return;
        stompClient.publish({destination: "/app/message/list", body: selectedChatId});
    }, [stompClient, selectedChatId]);

    /**
     * Paint the transcript IMMEDIATELY over REST, without waiting for the socket.
     *
     * The STOMP request above cannot even be sent until the WebSocket has finished its handshake and
     * CONNECT — on a fresh load (opening a chat straight from the history page) that left the pane
     * blank for seconds. This fetch runs in parallel and usually wins; the STOMP push then supersedes
     * it and keeps the conversation live. Results are ignored if the operator switched chats in the
     * meantime, and it never clobbers a transcript the socket already delivered.
     */
    useEffect(() => {
        if (!selectedChatId) return;
        let cancelled = false;
        const chatIdAtFetch = selectedChatId;

        // Re-opening a chat must re-read it. The old test was "this chat's cache is non-empty",
        // which only meant "the socket answered" back when the push wiped the whole map. Now that
        // the map accumulates, that test was true for every chat already visited — so a revisit
        // kept whatever was on screen when the operator left, silently missing everything since.
        // Stale-but-plausible is worse than blank: an operator can reply to it.
        messageAnsweredRef.current[chatIdAtFetch] = false;
        // Attachments belong to the conversation they were picked in. Carrying them across a
        // switch sent them to the wrong customer — or to a web chat that cannot render them.
        setFileList([]);

        fetchMessagesByChatId(chatIdAtFetch)
            .then((res) => {
                if (cancelled || selectedChatIdRef.current !== chatIdAtFetch) return;
                const rows = res?.content || res?.data?.content || (Array.isArray(res) ? res : []);
                if (!rows.length) return;
                setMessagesByChat((prev) => {
                    // The socket answered for THIS chat while we were fetching — it is fresher.
                    if (messageAnsweredRef.current[chatIdAtFetch]) return prev;
                    return {...prev, [chatIdAtFetch]: rows};
                });
            })
            .catch(() => {
                /* the STOMP path is still coming; a failed prefetch must not surface as an error */
            });

        return () => {
            cancelled = true;
        };
    }, [selectedChatId]);

    /**
     * Re-read everything when the operator comes back to the tab.
     *
     * The two fallbacks above only fire when the socket never answered. This is the other failure:
     * the socket DID answer, long ago, and then died while the tab was frozen — so the guards
     * ("the socket already delivered this") are precisely what keeps the stale screen in place.
     * The return to the tab is the one moment we know something may have been missed, so this
     * bypasses the guards and REPLACES the list and the open transcript over REST, then asks the
     * socket for the transcript again in case it is still alive. Cause-agnostic on purpose: it does
     * not care why the feed went quiet, only that we were away. A brief alt-tab costs nothing.
     */
    useEffect(() => {
        const MIN_ABSENCE_MS = 15 * 1000;
        return onReturnToTab((hiddenMs) => {
            if (hiddenMs < MIN_ABSENCE_MS) return;

            fetchMyChatList()
                .then((res) => {
                    const rows = Array.isArray(res) ? res : (Array.isArray(res?.data) ? res.data : null);
                    if (!rows || !rows.length) return;
                    setChatList(rows);
                    setChatListLoaded(true);
                })
                .catch(() => { /* the socket refresh may still deliver */ });

            const chatId = selectedChatIdRef.current;
            if (!chatId) return;

            fetchMessagesByChatId(chatId)
                .then((res) => {
                    if (selectedChatIdRef.current !== chatId) return;
                    const rows = res?.content || res?.data?.content || (Array.isArray(res) ? res : []);
                    if (!rows.length) return;
                    setMessagesByChat((prev) => ({...prev, [chatId]: rows}));
                })
                .catch(() => { /* best-effort */ });

            if (stompClient?.connected) {
                try {
                    stompClient.publish({destination: "/app/message/list", body: chatId});
                } catch (e) { /* a dead socket is being replaced by the dashboard anyway */ }
            }
        });
    }, [stompClient]);

    /**
     * صف تریاژ به‌جای فید «آخرین فعالیت».
     * بک‌اند لیست را با ORDER BY updatedAt DESC می‌فرستد و @PreUpdate با پاسخ خودِ اپراتور هم
     * updatedAt را جلو می‌برد؛ یعنی پاسخ دادن، چت را به بالای لیست می‌آورد و کاربری که ۲۰ دقیقه
     * در صف مانده پایین‌تر می‌رود. اینجا ترتیب را برعکسِ آن، بر اساس اولویت رسیدگی می‌چینیم.
     */
    const orderedChats = useMemo(() => {
        const ts = (c: any) => (c?.updatedAt ? new Date(c.updatedAt).getTime() : 0);
        return [...chatList].sort((a: any, b: any) => {
            const aWaiting = a.state === "WAITING";
            const bWaiting = b.state === "WAITING";
            if (aWaiting !== bWaiting) return aWaiting ? -1 : 1;   // صف انتظار همیشه بالاتر
            if (aWaiting) return ts(a) - ts(b);                    // در صف: قدیمی‌ترین اول
            const aUnread = a.unread ? 1 : 0;
            const bUnread = b.unread ? 1 : 0;
            if (aUnread !== bUnread) return bUnread - aUnread;     // سپس خوانده‌نشده‌ها
            return ts(b) - ts(a);                                  // در نهایت: تازه‌ترین اول
        });
    }, [chatList]);

    const queueCounts = useMemo(
        () => ({
            waiting: (chatList).filter((c: any) => c.state === "WAITING").length,
            unread: (chatList).filter((c: any) => c.unread).length,
        }),
        [chatList]
    );

    useEffect(() => {
        if (chatId) {
            setSelectedChatId(chatId);
            return;
        }

        // OP-8: انتخاب خودکارِ اولین گفت‌وگو فقط روی دسکتاپ. روی موبایل (تک‌ستونه) این کار
        // کاربر را بی‌درنگ از لیست رد می‌کرد و مستقیم داخل یک گفت‌وگو می‌انداخت.
        if (isDesktop && !chatId && !selectedChatId && orderedChats.length > 0) {
            const firstId = (orderedChats[0]).id;
            setSelectedChatId(firstId);
            navigate(`/dashboard/chats/${firstId}`, {replace: true});
        }
    }, [chatId, orderedChats, selectedChatId, navigate, isDesktop]);

    // cleanup ویس
    useEffect(() => {
        return () => {
            if (recordTimerRef.current) {
                clearInterval(recordTimerRef.current);
                recordTimerRef.current = null;
            }
            if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
                mediaRecorderRef.current.stop();
            }
        };
    }, []);

    const selectedChat = useMemo(
        () =>
            (chatList).find((c) => c.id === selectedChatId) ||
            preloadedChat ||
            null,
        [chatList, selectedChatId, preloadedChat]
    );
    const messages = messagesByChat[selectedChatId || ""] || [];
    // The newest message's id, so the scroll effect can tell "a different transcript arrived" from
    // "the same one was re-pushed after an edit" — a length comparison alone cannot.
    const newestMessageId = messages.length ? messages[messages.length - 1]?.id : null;

    /**
     * OP-12: برچسب حباب‌های اپراتور. ChatDTO.operatorId مالکِ گفت‌وگوست؛ فقط وقتی با اپراتورِ
     * واردشده یکی باشد «شما» درست است. برای گفت‌وگویی که به همکار دیگری تعلق دارد (مثلاً از
     * تاریخچه باز شده) نام همان اپراتور نمایش داده می‌شود.
     */
    const operatorBubbleLabel = useMemo(() => {
        const ownerId = selectedChat?.operatorId;
        if (me?.id && ownerId && String(ownerId) === String(me.id)) {
            return "شما (اپراتور)";
        }
        return selectedChat?.operatorName || "اپراتور";
    }, [selectedChat, me]);

    const isChatFinished =
        selectedChat &&
        ["AGENT_FINISHED", "OPERATOR_FINISHED", "TIME_OUT"].includes(
            selectedChat.state
        );

    /**
     * The widget transcript carries text only: a file-only or voice reply to a website visitor is
     * saved, stamped "sent" and rendered in this console — and never reaches the visitor. Until the
     * widget can render attachments, the honest thing is not to offer the buttons on a web chat.
     */
    const isWebChat = selectedChat?.channel === "WEB";
    const WEB_ATTACHMENT_HINT = "ارسال فایل و پیام صوتی در چت سایت هنوز پشتیبانی نمی‌شود؛ لطفاً متن بفرستید.";

    /**
     * چتی که انتخاب شده اما دیگر در لیست نیست (پایان‌یافته یا منتقل‌شده). قبلاً هر سه ستون
     * unmount می‌شدند و اپراتور با یک صفحه‌ی سفید می‌ماند.
     */
    const conversationEnded = chatListLoaded && !!selectedChatId && !selectedChat;

    // ارسال، REST است ولی اکوی پیام فقط از STOMP می‌آید؛ با سوکت قطع، پیام واقعاً می‌رود اما
    // در ترنسکریپت اپراتور دیده نمی‌شود و دوباره ارسال می‌شود. پس با قطعی سوکت ارسال را می‌بندیم.
    const canSend =
        (messageText.trim() !== "" || fileList.length > 0) &&
        !isChatFinished &&
        !isUploadingFiles &&
        !sending &&
        !!stompClient;

    useEffect(() => {
        if (!messagesContainerRef.current) return;

        const el = messagesContainerRef.current;
        el.scrollTop = el.scrollHeight;
        // Keyed on the NEWEST message rather than the array identity or its length.
        //
        // Identity fired on every server push, so correcting a message the operator had scrolled up
        // to find yanked the transcript to the bottom and lost their place. But length alone was
        // worse in a different way: opening a chat whose transcript happens to be the same length as
        // the last one is a wholesale replace with no length change, and the view would stay where
        // it was — showing the wrong conversation's scroll position, often the oldest message.
    }, [messages.length, newestMessageId, selectedChatId]);

    const handleSelectChat = (chatId: string) => {
        const chat = (chatList).find(c => c.id === chatId);

        if (chat?.unread) {
            markChatAsRead(chatId);
        }

        setChatList((prev: any[]) =>
            prev.map((c: any) =>
                c.id === chatId ? {...c, unread: false} : c
            )
        );

        setSelectedChatId(chatId);
        navigate(`/dashboard/chats/${chatId}`);
    };

    /** OP-8: بازگشت از ترنسکریپت به لیست چت‌ها در حالت تک‌ستونهٔ موبایل. */
    const handleBackToList = () => {
        setSelectedChatId(null);
        navigate("/dashboard/chats", {replace: true});
    };

    /** خروج دستی از گفت‌وگوی پایان‌یافته: انتخاب اولین چتِ صف (اولویت‌دارترین). */
    const handleGoToNextChat = () => {
        const next = (orderedChats)[0];
        if (next) {
            handleSelectChat(next.id);
            return;
        }
        setSelectedChatId(null);
        navigate("/dashboard/chats", {replace: true});
    };

    // An open editor belongs to one conversation. Leaving it open across a chat switch meant the
    // draft reappeared on whatever message happened to share that id, ready to overwrite it.
    useEffect(() => {
        setEditingId(null);
        setEditText("");
    }, [selectedChatId]);

    const beginEdit = (msg) => {
        setEditingId(msg.id);
        setEditText(msg.plainText || "");
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditText("");
    };

    /**
     * Saves a correction.
     *
     * Deliberately NOT optimistic, unlike sending. A send that silently fails leaves the operator
     * with an obviously missing message; an edit that silently fails leaves them looking at the
     * corrected text while the customer still reads the original — the one outcome this feature must
     * never produce. So we wait for the server, and only then let the transcript push settle it.
     */
    const saveEdit = async (msg) => {
        const text = (editText || "").trim();
        if (!text) {
            message.warning("متن پیام نمی‌تواند خالی باشد.");
            return;
        }
        if (text === (msg.plainText || "")) {
            cancelEdit();
            return;
        }
        setEditSaving(true);
        // Captured like handleSendMessage captures chatIdAtSend: a chat switch mid-flight must not
        // let the answer land in another conversation's transcript.
        const chatIdAtEdit = selectedChatId;
        try {
            const res = await editMessageText(msg.id, text);
            cancelEdit();
            // Apply the server's own answer to our copy.
            //
            // The transcript push normally arrives a moment later and supersedes this. But it travels
            // over STOMP, and if that socket is down the push never comes — leaving the operator
            // reading the OLD text after a correction the customer has already received. That is the
            // precise inversion this feature exists to prevent, so we do not rely on the socket to
            // learn the outcome of a call we made ourselves.
            const saved = res?.data ?? res;
            if (saved?.id) {
                setMessagesByChat((prev) => ({
                    ...prev,
                    [chatIdAtEdit]: (prev?.[chatIdAtEdit] || []).map((m) =>
                        m.id === saved.id
                            ? {...m, plainText: saved.plainText, editedAt: saved.editedAt}
                            : m),
                }));
            }
        } catch (e) {
            // The backend explains WHY in Persian — an old Telegram message that can no longer be
            // corrected, an expired window, a chat that is not ours. Showing our own generic text
            // instead would throw away the only useful part.
            const reason =
                e?.response?.data?.message ||
                e?.response?.data?.error ||
                "ویرایش پیام انجام نشد.";
            message.error(reason);
        } finally {
            setEditSaving(false);
        }
    };

    const handleSendMessage = async () => {
        if (isChatFinished) return;
        if (!canSend || !selectedChatId) return;
        if (!me || !me.id) return;
        // The composer's attachment list is shared across chats: a file picked in a Telegram chat
        // would otherwise ride along into a web chat, where the widget cannot show it and the
        // visitor would silently get nothing while the console shows "sent".
        if (isWebChat && fileList.length > 0) {
            message.warning(WEB_ATTACHMENT_HINT);
            return;
        }

        setSending(true);

        try {
            const attachments = fileList.map((f) => ({
                filePath: f.filePath,
                fileName: f.fileName,
                fileExtension: f.fileExtension,
                downloadUrl: f.downloadUrl,
                isVoice: f.isVoice === true
            }));

            const dto = {
                chatId: selectedChatId,
                jobId: null,
                status: "done",
                sender: "OPERATOR",
                plainText: messageText.trim() || null,
                files: attachments.length
                    ? attachments
                    : null,
            };

            // Optimistic echo: paint the bubble immediately instead of waiting for the POST to
            // return and the STOMP transcript to come back — that round-trip is what made the
            // operator's own message feel slow (and, for an admin viewing a chat they are not
            // assigned to, never appear at all). The server push replaces this list wholesale a
            // moment later, so the temporary row is superseded rather than duplicated.
            const optimisticId = `tmp-${Date.now()}`;
            const chatIdAtSend = selectedChatId;
            setMessagesByChat((prev) => ({
                ...prev,
                [chatIdAtSend]: [
                    ...(prev?.[chatIdAtSend] || []),
                    {
                        id: optimisticId,
                        chatId: chatIdAtSend,
                        sender: "OPERATOR",
                        plainText: dto.plainText,
                        files: dto.files,
                        createdAt: new Date().toISOString(),
                        pending: true,
                    },
                ],
            }));

            // Kept so a failure can hand them back. The composer is cleared optimistically because
            // that is what makes sending feel instant, but an operator reported the other half of
            // that bargain being broken: the send failed, the box was already empty, and the whole
            // reply had to be typed again from memory. Clearing on optimism only works if failure
            // undoes it.
            const sentText = messageText;
            const sentFiles = fileList;
            setMessageText("");
            setFileList([]);

            try {
                await createMessage(dto);
            } catch (err) {
                // Roll the optimistic bubble back so the operator is never told a message was sent
                // when it was not.
                setMessagesByChat((prev) => ({
                    ...prev,
                    [chatIdAtSend]: (prev?.[chatIdAtSend] || []).filter((m) => m.id !== optimisticId),
                }));

                // Give them their words back, exactly as typed, if they are still in this chat. A
                // chat switch mid-flight means the text belongs to a conversation they have left,
                // and pasting it into the new one would be worse than losing it.
                if (selectedChatIdRef.current === chatIdAtSend) {
                    setMessageText((current) => (current ? current : sentText));
                    setFileList((current) => (current && current.length ? current : sentFiles));
                }

                // "Failed" is not something we actually know. A request that dies without a reply —
                // a dropped connection, a sleeping laptop — may well have been processed: the server
                // log showed these arriving and being accepted while the operator was being told
                // they had not. So when there is no reply, re-read the transcript and say what is
                // really true, which is that we do not know and they can see for themselves.
                //
                // One notification, chosen rather than branched into, because a branch that can
                // silently produce none is exactly how somebody ends up staring at a composer with
                // no idea what happened.
                const serverAnswered = !!err?.response;
                if (!serverAnswered) {
                    fetchMessagesByChatId(chatIdAtSend)
                        .then((res) => {
                            const rows = res?.content || res?.data?.content || (Array.isArray(res) ? res : []);
                            if (!rows.length || selectedChatIdRef.current !== chatIdAtSend) return;
                            setMessagesByChat((prev) => ({...prev, [chatIdAtSend]: rows}));
                        })
                        .catch(() => {});
                }

                const notice = serverAnswered
                    ? (err?.response?.data?.message
                        || err?.response?.data?.error
                        || "ارسال پیام ناموفق بود. متن شما برگردانده شد؛ دوباره تلاش کنید.")
                    : "ارتباط قطع شد و نتیجه‌ی ارسال مشخص نیست. متن شما برگردانده شد — اگر پیام در گفت‌وگو دیده می‌شود ارسال شده، در غیر این صورت دوباره بفرستید.";
                message.open({type: serverAnswered ? "error" : "warning", content: notice, duration: 8});

                throw err;
            }
        } catch (e) {
            console.error("Error creating message", e);
        } finally {
            setSending(false);
        }
    };

    /**
     * اعلام «اپراتور در حال تایپ است» به بازدیدکننده. فقط یک نشانگر است — متنِ اپراتور هرگز
     * فرستاده نمی‌شود، تنها chatId می‌رود. throttle لبه‌ی جلو + یک ارسال دنباله‌رو، تا تایپ سریع
     * سوکت را پر نکند ولی آخرین ضربه هم گم نشود.
     */
    const publishOperatorTyping = () => {
        if (!stompClient || !selectedChatId) return;

        const chatIdAtType = selectedChatId;
        const send = () => {
            // بین زمان‌بندی و اجرا ممکن است اپراتور گفت‌وگو را عوض کرده باشد.
            if (!stompClient || selectedChatIdRef.current !== chatIdAtType) return;
            try {
                stompClient.publish({destination: "/app/chat/typing", body: chatIdAtType});
            } catch (e) {
                /* نشانگر تایپ اختیاری است — هرگز نباید کامپوزر را بشکند */
            }
        };

        const state = operatorTypingRef.current;
        const elapsed = Date.now() - state.last;
        if (elapsed >= TYPING_PUBLISH_THROTTLE_MS) {
            state.last = Date.now();
            send();
            return;
        }
        if (state.timer) return; // یک ارسال دنباله‌رو از قبل در صف است
        state.timer = window.setTimeout(() => {
            state.timer = null;
            state.last = Date.now();
            send();
        }, TYPING_PUBLISH_THROTTLE_MS - elapsed);
    };

    const handleComposerChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setMessageText(e.target.value);
        publishOperatorTyping();
    };

    // ✅ فقط همین اضافه شده: Enter = ارسال / Shift+Enter = خط جدید
    const handleTextAreaKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const handleRemoveFile = (idx: number) => {
        setFileList((prev) => prev.filter((_, i) => i !== idx));
    };

    const handleEndChat = () => {
        if (!selectedChatId) return;

        Modal.confirm({
            title: "پایان چت",
            content: "آیا از پایان این چت مطمئن هستید؟",
            okText: "بله، پایان چت",
            cancelText: "انصراف",
            onOk: () => {
                closeChat(selectedChatId);

                setChatList((prev: any[]) =>
                    prev.map((c: any) =>
                        c.id === selectedChatId
                            // همان وضعیتی که بک‌اند می‌نویسد؛ "CLOSED" عضو ChatStates نیست.
                            ? {...c, state: "OPERATOR_FINISHED"}
                            : c
                    )
                );
            },
        });
    };

    const handleOpenTransferModal = () => {
        if (!selectedChatId) {
            message.warning("ابتدا یک چت را انتخاب کنید.");
            return;
        }

        setTransferModalOpen(true);

        fetchOperatorsAfterTime(undefined, {
            onSuccess: (res) => {
                const list = res?.data ?? res ?? [];
                setTransferOperators(list);
            },
            onError: () => {
                message.error("خطا در دریافت لیست اپراتورها.");
            },
        });
    };

    const handleConfirmTransfer = () => {
        if (!selectedChatId) {
            message.warning("چت فعالی انتخاب نشده است.");
            return;
        }
        if (!transferTargetOperatorId) {
            message.warning("لطفاً اپراتور مقصد را انتخاب کنید.");
            return;
        }

        transferChat(
            {chatId: selectedChatId, operatorId: transferTargetOperatorId},
            {
                onError: () => {
                    message.error("خطا در انتقال چت. لطفاً دوباره تلاش کنید.");
                },
            }
        );
    };

    /* ---------- ویس: شروع ضبط (خروجی با پسوند .ogg) ---------- */
    const startRecording = async () => {
        if (!selectedChatId) {
            message.warning("ابتدا یک چت را انتخاب کنید.");
            return;
        }

        if (isChatFinished) {
            message.warning("این چت پایان یافته است و امکان ارسال پیام جدید وجود ندارد.");
            return;
        }

        if (isRecording) return;

        if (typeof window === "undefined" || !navigator.mediaDevices) {
            message.error("مرورگر شما از ضبط صدا پشتیبانی نمی‌کند.");
            return;
        }

        try {
            const stream = await navigator.mediaDevices.getUserMedia({audio: true});

            // می‌تونیم همچنان سعی کنیم mimeType خوب بدیم، ولی در نهایت خروجی رو .ogg می‌سازیم
            const preferredTypes = [
                "audio/ogg;codecs=opus",
                "audio/webm;codecs=opus",
                "audio/webm",
            ];

            let chosenType: string | undefined;
            if (typeof MediaRecorder !== "undefined" && (MediaRecorder).isTypeSupported
            ) {
                for (const t of preferredTypes) {
                    if ((MediaRecorder
                    ).isTypeSupported(t)
                    ) {
                        chosenType = t;
                        break;
                    }
                }
            }

            const options = chosenType ? {mimeType: chosenType} : undefined;
            const mediaRecorder = new MediaRecorder(stream, options
                )
            ;
            const chunks: BlobPart[] = [];

            mediaRecorderRef.current = mediaRecorder;

            mediaRecorder.ondataavailable = (event: BlobEvent) => {
                if (event.data && event.data.size > 0) {
                    chunks.push(event.data);
                }
            };

            mediaRecorder.onstop = async () => {
                if (recordTimerRef.current) {
                    clearInterval(recordTimerRef.current);
                    recordTimerRef.current = null;
                }
                setIsRecording(false);
                setRecordElapsed(0);

                // استریم را آزاد کن
                stream.getTracks().forEach((t) => t.stop());

                if (!chunks.length) {
                    return;
                }

                // اینجا عمداً type و پسوند را ogg می‌گذاریم تا تلگرام به‌عنوان ویس ببیند
                const ext = "ogg";
                const mimeForFile = "audio/ogg";

                const blob = new Blob(chunks, {type: mimeForFile});
                const fileName = `voice-${Date.now()}.${ext}`;
                const file = new File([blob], fileName, {type: mimeForFile});

                setUploadingCount((c) => c + 1);
                try {
                    const res = await uploadFileToMinio(file);
                    const data = res || {};

                    const meta = {
                        filePath: data?.filePath,
                        fileName: data?.fileName || file.name,
                        fileExtension: data?.fileExtension || ext,
                        downloadUrl: data?.downloadUrl || data?.url || "#",
                        isVoice: true, // 👈 فلگ کمکی برای بک‌اند
                    };

                    if (selectedChatId && me?.id) {
                        const dto = {
                            chatId: selectedChatId,
                            jobId: null,
                            status: "done",
                            sender: "OPERATOR",
                            plainText: null,
                            files: [meta],
                        };
                        setSending(true);
                        try {
                            await createMessage(dto);
                        } catch (err) {
                            console.error("Error sending voice message", err);
                        } finally {
                            setSending(false);
                        }
                    }
                } catch (err) {
                    console.error("❌ Error uploading voice to Minio", err);
                    message.error("خطا در آپلود ویس.");
                } finally {
                    setUploadingCount((c) => c - 1);
                }
            };

            mediaRecorder.start();
            setIsRecording(true);
            setRecordElapsed(0);

            const start = Date.now();
            recordTimerRef.current = window.setInterval(() => {
                setRecordElapsed(Date.now() - start);
            }, 100);
        } catch (err) {
            console.error("Error accessing microphone", err);
            message.error("دسترسی به میکروفون امکان‌پذیر نیست.");
        }
    };

    /* ---------- ویس: پایان ضبط ---------- */
    const stopRecording = () => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
            mediaRecorderRef.current.stop();
        }
        if (recordTimerRef.current) {
            clearInterval(recordTimerRef.current);
            recordTimerRef.current = null;
        }
    };

    return (
        // On a phone this screen IS the app: it takes the whole height it is given and never lets
        // the page itself scroll, so the transcript scrolls under a fixed header and a reply box
        // that stays put. The padding is dropped too — 16px a side out of 375 is a tenth of the
        // screen spent on nothing.
        <div dir="rtl" className="w-full p-0 md:p-6" style={{height: isDesktop ? undefined : "100%"}}>
            <Card
                className="shadow-sm rounded-2xl border border-gray-100"
                style={{
                    borderRadius: isDesktop ? 12 : 0,
                    overflow: "hidden",
                    maxWidth: 1400,
                    margin: "0 auto",
                    height: isDesktop ? undefined : "100%",
                }}
                bodyStyle={{padding: 0, height: isDesktop ? undefined : "100%"}}
            >
                <div
                    style={{
                        display: isDesktop ? "grid" : "flex",
                        gridTemplateColumns: isDesktop ? "280px 1fr" : undefined,
                        flexDirection: isDesktop ? undefined : "column",
                        height: isDesktop ? undefined : "100%",
                        minHeight: 0,
                    }}
                >
                    {/* ===== ستون لیست چت‌ها =====
                        OP-8: زیر بریک‌پوینت md فقط یک ستون در هر لحظه دیده می‌شود — لیست تا وقتی
                        گفت‌وگویی انتخاب نشده، و ترنسکریپت پس از انتخاب. روی دسکتاپ هر دو ستون. */}
                    {(isDesktop || !selectedChatId) && (
                    <div
                        style={{
                            borderLeft: isDesktop ? `1px solid ${HAIRLINE}` : "none",
                            borderBottom: !isDesktop ? `1px solid ${HAIRLINE}` : "none",
                            padding: isDesktop ? 10 : 8,
                            background: SURFACE,
                            display: "flex",
                            flexDirection: "column",
                            // On a phone the list is the whole screen and scrolls inside itself.
                            // As `auto` it grew to the length of the caseload and took the page
                            // with it, so the search box at the top scrolled away and there was no
                            // way back to it without flicking to the top of forty conversations.
                            height: isDesktop ? CHAT_HEIGHT : "100%",
                            minHeight: isDesktop ? "70vh" : 0,
                            boxSizing: "border-box",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                marginBottom: 8,
                                gap: 8,
                            }}
                        >
                            <Space>
                                <MenuOutlined/>
                                <Title level={5} style={{margin: 0, fontSize: isDesktop ? 16 : 14}}>
                                    لیست چت‌ها
                                </Title>
                            </Space>

                            {/* شمارنده‌های صف + وضعیت اتصال زنده */}
                            <Space size={4} wrap>
                                {/* "Connecting" is only honest while a reconnect can actually
                                    succeed. Once renewal has failed terminally the socket will
                                    never come back on its own, and saying otherwise is what let an
                                    operator sit for hours believing they were online. */}
                                {!stompClient && (
                                    <Tag
                                        color="error"
                                        style={{margin: 0, fontSize: 10, lineHeight: "16px", padding: "0 5px"}}
                                    >
                                        {sessionDead
                                            ? "نشست منقضی شد — دوباره وارد شوید"
                                            : "آفلاین — در حال اتصال…"}
                                    </Tag>
                                )}
                                {queueCounts.waiting > 0 && (
                                    <Tooltip title="گفت‌وگوهای در صف انتظار">
                                        <Tag
                                            color="orange"
                                            style={{margin: 0, fontSize: 10, lineHeight: "16px", padding: "0 5px"}}
                                        >
                                            {faDigits(queueCounts.waiting)} در صف
                                        </Tag>
                                    </Tooltip>
                                )}
                                {queueCounts.unread > 0 && (
                                    <Tooltip title="گفت‌وگوهای خوانده‌نشده">
                                        <Tag
                                            color="red"
                                            style={{margin: 0, fontSize: 10, lineHeight: "16px", padding: "0 5px"}}
                                        >
                                            {faDigits(queueCounts.unread)} خوانده‌نشده
                                        </Tag>
                                    </Tooltip>
                                )}
                            </Space>
                        </div>

                        {/* minHeight:0 is what actually lets this scroll. A flex child defaults to
                            min-height:auto — it refuses to shrink below its content — so without it
                            `flex:1` grows the list to the length of the caseload and the scrollbar
                            lands on the page rather than in here. */}
                        <div style={{flex: 1, minHeight: 0, overflowY: "auto", overscrollBehavior: "contain", paddingRight: 4}}>
                            <List
                                dataSource={orderedChats}
                                renderItem={(chat: any) => {
                                    const isSelected = chat.id === selectedChatId;
                                    const isWaiting = chat.state === "WAITING";
                                    const chMeta = channelMeta(chat.channel);
                                    const stMeta = stateMeta(chat.state);
                                    const ageMs = chat.updatedAt
                                        ? nowTs - new Date(chat.updatedAt).getTime()
                                        : 0;
                                    // فقط انتظارِ طولانی هشدار است؛ چت در حال پاسخ‌گویی سنِ عادی دارد.
                                    const overdue = isWaiting && ageMs > WAIT_ALERT_MS;

                                    return (
                                        <List.Item
                                            className="cursor-pointer"
                                            style={{
                                                padding: "8px 10px",
                                                marginBottom: 4,
                                                borderRadius: 10,
                                                background: isSelected ? "#fff8e1" : "transparent",
                                                border: isSelected
                                                    ? "1px solid #ffe082"
                                                    : "1px solid transparent",
                                                // نوار کناری برای ردیف خوانده‌نشده (بعد از border می‌آید تا آن را بازنویسی کند)
                                                borderInlineStart: chat.unread
                                                    ? "3px solid #e53935"
                                                    : undefined,
                                            }}
                                            onClick={() => handleSelectChat(chat.id)}
                                        >
                                            <List.Item.Meta
                                                avatar={
                                                    <Badge dot={!!chat.unread} offset={[-2, 2]}>
                                                        <Avatar
                                                            size={isDesktop ? "default" : "small"}
                                                            style={{
                                                                backgroundColor: "#ffd54f",
                                                                color: "#4e342e",
                                                            }}
                                                        >
                                                            {getInitials(chat.fullName)}
                                                        </Avatar>
                                                    </Badge>
                                                }
                                                title={
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            justifyContent: "space-between",
                                                            gap: 8,
                                                            alignItems: "center",
                                                        }}
                                                    >
                                                    <span
                                                        style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 5,
                                                            minWidth: 0,
                                                        }}
                                                    >
                                                        {/* Channel at a glance — a web visitor and a
                                                            Telegram user need different handling. */}
                                                        <Tooltip title={chMeta.label}>
                                                            <span
                                                                style={{
                                                                    fontSize: 11,
                                                                    color: chMeta.color === "green"
                                                                        ? "var(--npe-accent)"
                                                                        : themeToken.colorInfo,
                                                                    flexShrink: 0,
                                                                }}
                                                            >
                                                                {chMeta.icon}
                                                            </span>
                                                        </Tooltip>
                                                        <span
                                                            style={{
                                                                fontSize: isDesktop ? 13 : 12,
                                                                fontWeight: chat.unread ? 700 : 500,
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis",
                                                                whiteSpace: "nowrap",
                                                            }}
                                                        >
                                                            {chat.fullName}
                                                        </span>
                                                        {langLabel(chat.lang) && (
                                                            <Tag
                                                                color={foreignLangLabel(chat.lang) ? "blue" : undefined}
                                                                style={{
                                                                    fontSize: 9,
                                                                    lineHeight: "14px",
                                                                    padding: "0 4px",
                                                                    margin: 0,
                                                                    flexShrink: 0,
                                                                }}
                                                            >
                                                                {langLabel(chat.lang)}
                                                            </Tag>
                                                        )}
                                                    </span>
                                                        {/* سنِ نسبی به‌جای تاریخ مطلق؛ تاریخ دقیق در Tooltip می‌ماند. */}
                                                        <Tooltip title={faDateWithHour(chat.updatedAt)}>
                                                            <span
                                                                style={{
                                                                    fontSize: 10,
                                                                    whiteSpace: "nowrap",
                                                                    flexShrink: 0,
                                                                    color: overdue ? "#e53935" : "#999",
                                                                    fontWeight: overdue ? 700 : 400,
                                                                }}
                                                            >
                                                                {relAge(chat.updatedAt, nowTs)}
                                                            </span>
                                                        </Tooltip>
                                                    </div>
                                                }
                                                description={
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 6,
                                                            minWidth: 0,
                                                        }}
                                                    >
                                                        <Tag
                                                            color={stMeta.color}
                                                            style={{
                                                                fontSize: 9,
                                                                lineHeight: "14px",
                                                                padding: "0 4px",
                                                                margin: 0,
                                                                flexShrink: 0,
                                                            }}
                                                        >
                                                            {stMeta.label}
                                                        </Tag>
                                                        <Text
                                                            style={{
                                                                fontSize: isDesktop ? 11 : 10,
                                                                color: "#666",
                                                            }}
                                                            ellipsis
                                                        >
                                                            {chat.lastMessage}
                                                        </Text>
                                                    </div>
                                                }
                                            />
                                        </List.Item>
                                    );
                                }}
                            />
                        </div>
                    </div>
                    )}

                    {/* ===== ستون چت اصلی ===== */}
                    {(isDesktop || selectedChatId) && (
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            background: "var(--npe-surface-sunken)",
                            maxHeight: isDesktop ? CHAT_HEIGHT : "none",
                            // Fill the phone, then let the transcript inside take whatever is left
                            // over after the header and the reply box have had their share.
                            height: isDesktop ? undefined : "100%",
                            minHeight: isDesktop ? "70vh" : 0,
                        }}
                    >
                        {/* گفت‌وگویی که دیگر در لیست نیست (پایان‌یافته یا منتقل‌شده):
                            به‌جای صفحه‌ی سفید، یک وضعیت صریح با راه خروج. */}
                        {conversationEnded && (
                            <div
                                style={{
                                    flex: 1,
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 10,
                                    padding: 24,
                                    textAlign: "center",
                                    minHeight: isDesktop ? "auto" : 220,
                                }}
                            >
                                <Text style={{fontSize: 15, fontWeight: 600}}>
                                    این گفت‌وگو پایان یافت
                                </Text>
                                <Text type="secondary" style={{fontSize: 12, maxWidth: 320}}>
                                    این گفت‌وگو بسته یا به اپراتور دیگری منتقل شده و دیگر در لیست شما
                                    نیست.
                                </Text>
                                <Button
                                    type="primary"
                                    style={{borderRadius: 16}}
                                    onClick={handleGoToNextChat}
                                    disabled={orderedChats.length === 0}
                                >
                                    گفت‌وگوی بعدی
                                </Button>
                                {orderedChats.length === 0 && (
                                    <Text type="secondary" style={{fontSize: 11}}>
                                        در حال حاضر گفت‌وگوی بازی در لیست شما نیست.
                                    </Text>
                                )}
                            </div>
                        )}

                        {/* هدر */}
                        {selectedChat && (
                            <div
                                style={{
                                    padding: isDesktop ? "10px 16px" : "8px 10px",
                                    borderBottom: `1px solid ${HAIRLINE}`,
                                    display: "flex",
                                    flexWrap: "wrap",
                                    rowGap: 8,
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 8,
                                    background: SURFACE,
                                }}
                            >
                                <Space>
                                    {/* OP-8: تنها راه بازگشت به لیست در حالت تک‌ستونه. جهت فلش
                                        در RTL «به عقب» است. */}
                                    {!isDesktop && (
                                        <Button
                                            type="text"
                                            size="small"
                                            icon={<ArrowRightOutlined/>}
                                            onClick={handleBackToList}
                                            aria-label="بازگشت به لیست چت‌ها"
                                        />
                                    )}
                                    <Badge dot={selectedChat?.unread} offset={[-2, 2]}>
                                        <Avatar
                                            size={isDesktop ? "default" : "small"}
                                            style={{backgroundColor: "#ffd54f", color: "#4e342e"}}
                                        >
                                            {getInitials(selectedChat.fullName)}
                                        </Avatar>
                                    </Badge>
                                    <Space direction="vertical" size={0} style={{minWidth: 0}}>
                                        <Text
                                            className="font-medium"
                                            style={{fontSize: isDesktop ? 14 : 13}}
                                            ellipsis={!isDesktop}
                                        >
                                            {selectedChat.fullName}
                                        </Text>
                                        {/* Dropped on a phone. This line made the identity block
                                            218px wide next to 106px of buttons in a 296px header,
                                            so the two wrapped onto separate rows and the header
                                            took 107px — a seventh of the screen — to show a
                                            timestamp that is already on the row the operator just
                                            tapped, and in «اطلاعات چت». */}
                                        {isDesktop && (
                                            <Text type="secondary" style={{fontSize: 11}}>
                                                آخرین فعالیت: {faDateWithHour(selectedChat.updatedAt)}
                                            </Text>
                                        )}
                                    </Space>
                                </Space>

                                {/* Three labelled buttons fit a desktop header and wrap to a second
                                    row on a phone, where the header was costing 107px of an 812px
                                    screen before the first message was drawn. On a phone the two
                                    occasional actions move into an overflow menu — the ordinary
                                    mobile pattern — and only ending the chat, which is the one an
                                    operator reaches for constantly, keeps its own button. */}
                                {isDesktop ? (
                                    <Space wrap>
                                        <Button
                                            type="primary"
                                            danger
                                            onClick={handleEndChat}
                                            style={{borderRadius: 16}}
                                            disabled={isChatFinished}
                                        >
                                            پایان چت
                                        </Button>
                                        <Button
                                            onClick={handleOpenTransferModal}
                                            style={{borderRadius: 16}}
                                            disabled={isChatFinished}
                                        >
                                            انتقال چت
                                        </Button>
                                        <Button
                                            icon={<InfoCircleOutlined/>}
                                            onClick={() => setShowInfoModal(true)}
                                            style={{borderRadius: 16}}
                                        >
                                            اطلاعات چت
                                        </Button>
                                    </Space>
                                ) : (
                                    <Space size={4}>
                                        <Button
                                            type="primary"
                                            danger
                                            size="small"
                                            onClick={handleEndChat}
                                            style={{borderRadius: 16}}
                                            disabled={isChatFinished}
                                        >
                                            پایان چت
                                        </Button>
                                        <Dropdown
                                            trigger={["click"]}
                                            placement="bottomLeft"
                                            menu={{
                                                items: [
                                                    {
                                                        key: "transfer",
                                                        label: "انتقال چت",
                                                        icon: <SwapOutlined/>,
                                                        disabled: isChatFinished,
                                                        onClick: handleOpenTransferModal,
                                                    },
                                                    {
                                                        key: "info",
                                                        label: "اطلاعات چت",
                                                        icon: <InfoCircleOutlined/>,
                                                        onClick: () => setShowInfoModal(true),
                                                    },
                                                ],
                                            }}
                                        >
                                            <Button
                                                size="small"
                                                icon={<MoreOutlined/>}
                                                aria-label="سایر عملیات گفت‌وگو"
                                                style={{borderRadius: 16}}
                                            />
                                        </Dropdown>
                                    </Space>
                                )}
                            </div>
                        )}

                        {/* بدنه پیام‌ها */}
                        {selectedChat && (
                            <div
                                ref={messagesContainerRef}
                                style={{
                                    flex: 1,
                                    padding: isDesktop ? "12px 16px" : "8px 10px",
                                    // OP-8: روی موبایل ترنسکریپت جعبهٔ اسکرول خودش را دارد، وگرنه
                                    // کل صفحه یک طومار می‌شد و کامپوزر ته آن گم می‌شد.
                                    //
                                    // The mobile height used to be `calc(100vh - 210px)`: a guess at
                                    // the height of the header and reply box stacked around it, and
                                    // measured against a `100vh` that is not what a phone actually
                                    // shows. When the guess ran high the reply box was pushed off
                                    // the bottom of the screen; when it ran low the transcript left
                                    // a band of dead space under it. `flex: 1` with `minHeight: 0`
                                    // measures the real leftover space instead of predicting it.
                                    maxHeight: isDesktop ? CHAT_HEIGHT : "none",
                                    minHeight: 0,
                                    overflowY: "auto",
                                    overscrollBehavior: "contain",
                                    background: "var(--npe-surface-sunken)",
                                }}
                            >
                                {messages.map((msg: any) => {
                                    const meta = roleMeta(msg.sender, operatorBubbleLabel);
                                    const alignSelf =
                                        meta.align === "flex-end"
                                            ? {alignItems: "flex-end"}
                                            : {alignItems: "flex-start"};

                                    return (
                                        <div
                                            key={msg.id}
                                            style={{
                                                display: "flex",
                                                flexDirection:
                                                    meta.align === "flex-end" ? "row-reverse" : "row",
                                                gap: 8,
                                                marginBottom: 10,
                                                ...alignSelf,
                                            }}
                                        >
                                            <Avatar
                                                size={isDesktop ? "default" : "small"}
                                                style={{
                                                    backgroundColor: meta.avatarBg,
                                                    color: "#fff",
                                                    boxShadow: "0 2px 6px rgba(0,0,0,0.16)",
                                                }}
                                                icon={
                                                    meta.isUser ? (
                                                        <UserOutlined/>
                                                    ) : msg.sender === "AGENT" ? (
                                                        <RobotOutlined/>
                                                    ) : (
                                                        // OP-12: آیکون متفاوت از کاربر، تا رنگ تنها
                                                        // تفاوت‌دهندهٔ نقش نباشد.
                                                        <CustomerServiceOutlined/>
                                                    )
                                                }
                                            />
                                            <div style={{maxWidth: isDesktop ? "70%" : "85%"}}>
                                                <div
                                                    style={{
                                                        background: meta.bg,
                                                        border: meta.border,
                                                        borderRadius: 16,
                                                        padding: "8px 10px",
                                                        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: meta.isUser
                                                                ? "flex-end"
                                                                : "space-between",
                                                            gap: 6,
                                                            marginBottom: 4,
                                                        }}
                                                    >
                                                        <Text strong style={{fontSize: 12}}>
                                                            {meta.label}
                                                        </Text>
                                                        {/* Time, edited marker and the edit control
                                                            are wrapped together so this flex row still
                                                            has exactly TWO children — a third would
                                                            make space-between reshuffle the label and
                                                            the timestamp. */}
                                                        <Space size={2}>
                                                            {/* OP-12: مهرِ ساعت‌فقط؛ تاریخ کامل در
                                                                Tooltip می‌ماند و پرتکرارترین ناحیه‌ی
                                                                صفحه را شلوغ نمی‌کند. */}
                                                            <Tooltip title={faDateWithHour(msg.createdAt)}>
                                                                <span style={{fontSize: 11, color: "#777"}}>
                                                                    {fmtPersian(msg.createdAt, "HH:mm")}
                                                                </span>
                                                            </Tooltip>
                                                            {msg.editedAt && (
                                                                <Tooltip title={"ویرایش‌شده در " + faDateWithHour(msg.editedAt)}>
                                                                    <span style={{fontSize: 11, color: "#777"}}>
                                                                        · ویرایش‌شده
                                                                    </span>
                                                                </Tooltip>
                                                            )}
                                                            {/* canEdit is decided by the server. The
                                                                console cannot work it out on its own —
                                                                a message carries no author, and a
                                                                Telegram message sent before ids were
                                                                recorded cannot be corrected at all —
                                                                and a pencil the backend then refuses
                                                                is worse than no pencil at all. */}
                                                            {msg.canEdit && editingId !== msg.id && (
                                                                <Tooltip title="ویرایش پیام">
                                                                    <Button
                                                                        type="text"
                                                                        size="small"
                                                                        icon={<EditOutlined style={{fontSize: 12}}/>}
                                                                        onClick={() => beginEdit(msg)}
                                                                        style={{
                                                                            height: 18,
                                                                            width: 18,
                                                                            minWidth: 18,
                                                                            padding: 0,
                                                                            color: "#777",
                                                                        }}
                                                                    />
                                                                </Tooltip>
                                                            )}
                                                        </Space>
                                                    </div>
                                                    {editingId === msg.id ? (
                                                        <div>
                                                            <TextArea
                                                                value={editText}
                                                                onChange={(e) => setEditText(e.target.value)}
                                                                onKeyDown={(e) => {
                                                                    // Same keymap as the composer, so
                                                                    // the muscle memory carries over.
                                                                    if (e.key === "Escape") {
                                                                        e.preventDefault();
                                                                        cancelEdit();
                                                                    } else if (e.key === "Enter" && !e.shiftKey) {
                                                                        e.preventDefault();
                                                                        saveEdit(msg);
                                                                    }
                                                                }}
                                                                autoSize={{minRows: 1, maxRows: 6}}
                                                                autoFocus
                                                                style={{fontSize: 13}}
                                                            />
                                                            <Space size={4} style={{marginTop: 6}}>
                                                                <Button
                                                                    type="primary"
                                                                    size="small"
                                                                    loading={editSaving}
                                                                    onClick={() => saveEdit(msg)}
                                                                >
                                                                    ذخیره
                                                                </Button>
                                                                <Button size="small" onClick={cancelEdit}>
                                                                    انصراف
                                                                </Button>
                                                            </Space>
                                                        </div>
                                                    ) : (
                                                        msg.plainText && (
                                                            <Text
                                                                style={{
                                                                    fontSize: 13,
                                                                    whiteSpace: "pre-wrap",
                                                                }}
                                                            >
                                                                {msg.plainText}
                                                            </Text>
                                                        )
                                                    )}
                                                    {renderAttachments(msg.files, meta.isUser)}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* پیش‌نویسِ زندهٔ بازدیدکننده — هنوز ارسال نشده.
                            عمداً حباب نیست: بدون آواتار، بدون پس‌زمینه، بدون گوشه‌ی گرد و کم‌رنگ و
                            ایتالیک، تا با یک پیامِ رسیده اشتباه گرفته نشود. بیرون از جعبه‌ی اسکرولِ
                            ترنسکریپت است تا هم همیشه دیده شود و هم اسکرولِ اپراتور را تکان ندهد. */}
                        {selectedChat && !!visitorDraft && (
                            <div
                                style={{
                                    padding: isDesktop ? "6px 16px 8px" : "6px 10px 8px",
                                    background: "var(--npe-surface-sunken)",
                                    borderTop: `1px dashed ${HAIRLINE}`,
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 2,
                                }}
                            >
                                {/* aria-live فقط روی این خطِ ثابت است، نه روی خودِ پیش‌نویس؛ وگرنه
                                    اسکرین‌ریدر با هر کلیدِ بازدیدکننده دوباره متن را می‌خواند. */}
                                <Text
                                    type="secondary"
                                    aria-live="polite"
                                    style={{fontSize: 10, opacity: 0.75}}
                                >
                                    در حال نوشتن… (هنوز ارسال نشده)
                                </Text>
                                <Text
                                    type="secondary"
                                    italic
                                    style={{
                                        // span درون‌خطیِ Text با maxHeight/overflow کار نمی‌کند؛
                                        // بدون block، پیش‌نویسِ بلند کامپوزر را به پایین هل می‌داد.
                                        display: "block",
                                        fontSize: 12,
                                        opacity: 0.6,
                                        whiteSpace: "pre-wrap",
                                        wordBreak: "break-word",
                                        maxHeight: 54,
                                        overflowY: "auto",
                                    }}
                                >
                                    {visitorDraft}
                                </Text>
                            </div>
                        )}

                        {/* composer */}
                        {selectedChat && (
                            <div
                                style={{
                                    borderTop: `1px solid ${HAIRLINE}`,
                                    // One declaration, not a shorthand plus a stylesheet rule for
                                    // the bottom edge: an inline shorthand outranks the stylesheet,
                                    // so the rule that was supposed to clear the home indicator
                                    // silently lost every time. env() resolves to 0 unless the page
                                    // opts into viewport-fit=cover, which this one does not — the
                                    // browser insets the whole page for us — so this is a no-op
                                    // today and stays correct if that ever changes.
                                    padding: isDesktop
                                        ? 10
                                        : "8px 8px calc(8px + env(safe-area-inset-bottom, 0px))",
                                    background: SURFACE,
                                    // Never squeezed by a long transcript, never scrolled away.
                                    flexShrink: 0,
                                }}
                            >
                                <div
                                    style={{
                                        background: "var(--npe-surface-sunken)",
                                        borderRadius: 12,
                                        border: "1px solid #e0e0e0",
                                        padding: 8,
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 6,
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "flex-end",
                                            gap: 8,
                                        }}
                                    >
                                        {/* دکمه ویس + تایمر و پایان (سمت مخالف سنجاق/ارسال) */}
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 6,
                                                flexShrink: 0,
                                            }}
                                        >
                                            {/* The three composer controls were "small" on mobile
                                                and "middle" on desktop, which is exactly backwards:
                                                the small screen is the one being operated with a
                                                thumb. They are full size on a phone now. */}
                                            {!isRecording && (
                                                <Tooltip title={isWebChat ? WEB_ATTACHMENT_HINT : ""}>
                                                    <span style={{display: "inline-flex"}}>
                                                        <Button
                                                            type="text"
                                                            shape="circle"
                                                            size="middle"
                                                            icon={<AudioOutlined/>}
                                                            disabled={isWebChat}
                                                            onClick={startRecording}
                                                        />
                                                    </span>
                                                </Tooltip>
                                            )}

                                            {isRecording && (
                                                <Space size={4} align="center">
                                                    <Button
                                                        type="primary"
                                                        danger
                                                        size={isDesktop ? "small" : "small"}
                                                        onClick={stopRecording}
                                                        style={{borderRadius: 12}}
                                                    >
                                                        پایان
                                                    </Button>
                                                    <Text type="secondary" style={{fontSize: 11}}>
                                                        {formatRecordTime(recordElapsed)}
                                                    </Text>
                                                </Space>
                                            )}
                                        </div>

                                        {/* تکست اریا */}
                                        <div style={{flex: 1}}>
                                            <TextArea
                                                value={messageText}
                                                onChange={handleComposerChange}
                                                onKeyDown={handleTextAreaKeyDown} // 👈 فقط همین
                                                autoSize={{minRows: 1, maxRows: 4}}
                                                // AntD sizes an empty textarea to fit its
                                                // placeholder, so the long one wrapped to three
                                                // lines in the narrow phone composer and left a
                                                // 92px-tall empty reply box sitting there.
                                                // For a non-Persian customer the reply box itself says
                                                // which language to answer in — the one place the
                                                // operator is certainly looking while they type. Kept
                                                // short for the same placeholder-height reason.
                                                placeholder={
                                                    foreignLangLabel(selectedChat?.lang)
                                                        ? (isDesktop
                                                            ? `پاسخ به زبان ${foreignLangLabel(selectedChat.lang)}...`
                                                            : `${foreignLangLabel(selectedChat.lang)}…`)
                                                        : (isDesktop ? "پیام خود را بنویسید..." : "پیام…")
                                                }
                                                bordered={false}
                                                disabled={isChatFinished}
                                                style={{
                                                    boxShadow: "none",
                                                    background: "transparent",
                                                    // 16px on a phone is not a type-size choice.
                                                    // iOS zooms the page in whenever you focus a
                                                    // field smaller than that and does not zoom back
                                                    // out — so the operator was pinched into a
                                                    // magnified console every single time they went
                                                    // to answer somebody.
                                                    fontSize: isDesktop ? 13 : 16,
                                                }}
                                            />
                                        </div>

                                        {/* سنجاق + ارسال (سمت دیگر ردیف) */}
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 4,
                                                flexShrink: 0,
                                            }}
                                        >
                                            <Upload
                                                multiple
                                                showUploadList={false}
                                                disabled={isChatFinished || isWebChat}
                                                beforeUpload={(file) => {
                                                    setUploadingCount((c) => c + 1);

                                                    uploadFileToMinio(file)
                                                        .then((res) => {
                                                            const data = res || {};

                                                            const meta = {
                                                                filePath: data?.filePath,
                                                                fileName: data?.fileName || file.name,
                                                                fileExtension: data?.fileExtension,
                                                                downloadUrl: data?.downloadUrl || data.url || "#",
                                                            };

                                                            setFileList((prev) => [...prev, meta]);
                                                        })
                                                        .catch((err) => {
                                                            console.error("❌ Error uploading file to Minio", err);
                                                        })
                                                        .finally(() => {
                                                            setUploadingCount((c) => c - 1);
                                                        });

                                                    return false;
                                                }}
                                            >
                                                <Tooltip title={isWebChat ? WEB_ATTACHMENT_HINT : ""}>
                                                    <span style={{display: "inline-flex"}}>
                                                        <Button
                                                            type="text"
                                                            shape="circle"
                                                            size="middle"
                                                            icon={<PaperClipOutlined/>}
                                                            disabled={isChatFinished || isWebChat}
                                                        />
                                                    </span>
                                                </Tooltip>
                                            </Upload>

                                            <Tooltip
                                                title={
                                                    !stompClient
                                                        ? "ارتباط زنده قطع است؛ پس از برقراری اتصال دوباره تلاش کنید."
                                                        : ""
                                                }
                                            >
                                                {/* span لازم است تا Tooltip روی دکمه‌ی غیرفعال هم کار کند */}
                                                <span style={{display: "inline-flex"}}>
                                                    {/* Icon-only on a phone. With the label, this
                                                        button was 121px of a 262px row and the
                                                        reply box was left 87px — narrow enough that
                                                        its own placeholder wrapped onto three lines
                                                        and pushed the composer to 127px tall. The
                                                        paper plane is what every chat app puts
                                                        here; the label stays on desktop, where
                                                        there is room for it. */}
                                                    <Button
                                                        type="primary"
                                                        icon={<SendOutlined/>}
                                                        size="middle"
                                                        shape={isDesktop ? "default" : "circle"}
                                                        aria-label="ارسال پیام"
                                                        onClick={handleSendMessage}
                                                        disabled={isChatFinished || !canSend || sending}
                                                        loading={sending}
                                                        style={isDesktop
                                                            ? {borderRadius: 10, paddingInline: 16}
                                                            : undefined}
                                                    >
                                                        {isDesktop ? "ارسال" : null}
                                                    </Button>
                                                </span>
                                            </Tooltip>
                                        </div>
                                    </div>

                                    {!!fileList.length && (
                                        <div
                                            style={{
                                                borderTop: "1px solid #e0e0e0",
                                                paddingTop: 6,
                                                display: "flex",
                                                flexWrap: "wrap",
                                                gap: 6,
                                                maxHeight: isDesktop ? 80 : 100,
                                                overflowY: "auto",
                                            }}
                                        >
                                            {fileList.map((f, idx) => (
                                                <Tag
                                                    key={f.filePath || f.fileName || idx}
                                                    closable
                                                    onClose={(e) => {
                                                        e.preventDefault();
                                                        handleRemoveFile(idx);
                                                    }}
                                                    style={{
                                                        margin: 0,
                                                        paddingInline: 8,
                                                        maxWidth: 220,
                                                        fontSize: 11,
                                                    }}
                                                >
                                                    <span
                                                        style={{
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            whiteSpace: "nowrap",
                                                            display: "inline-block",
                                                            verticalAlign: "bottom",
                                                        }}
                                                    >
                                                        {f.fileName}
                                                    </span>
                                                </Tag>
                                            ))}
                                        </div>
                                    )}

                                    {isUploadingFiles && (
                                        <Text type="secondary" style={{fontSize: 11, marginRight: 8}}>
                                            در حال آپلود فایل...
                                        </Text>
                                    )}

                                    {sending && (
                                        <Text type="secondary" style={{fontSize: 11}}>
                                            در حال ارسال پیام...
                                        </Text>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                    )}
                </div>
            </Card>

            {/* مودال اطلاعات چت */}
            {selectedChat && (
                <Modal
                    open={showInfoModal}
                    // Unmount the body on close. Without it the visitor-journey panel inside keeps
                    // polling every ten seconds for the rest of the shift, rendering to nothing.
                    destroyOnClose
                    onCancel={() => setShowInfoModal(false)}
                    onOk={() => setShowInfoModal(false)}
                    okText="بستن"
                    cancelButtonProps={{style: {display: "none"}}}
                    title={
                        <Space>
                            <InfoCircleOutlined/>
                            <span>اطلاعات کاربر و خلاصه چت</span>
                        </Space>
                    }
                    width={520}
                >
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 12,
                        }}
                    >
                        <Space align="center">
                            <Avatar style={{backgroundColor: "#ffd54f", color: "#4e342e"}}>
                                {getInitials(selectedChat.user?.fullName || "")}
                            </Avatar>
                            <div>
                                <div style={{fontWeight: 600}}>
                                    {selectedChat.user?.fullName || "-"}
                                </div>
                                <div style={{fontSize: 12, color: "#888"}}>
                                    {selectedChat.user?.email || ""}
                                </div>
                            </div>
                        </Space>

                        <div
                            style={{
                                borderRadius: 12,
                                border: `1px solid ${HAIRLINE}`,
                                overflow: "hidden",
                                background: SURFACE,
                            }}
                        >
                            <div
                                style={{
                                    padding: "8px 12px",
                                    background: "var(--npe-surface-sunken)",
                                    borderBottom: `1px solid ${HAIRLINE}`,
                                    fontWeight: 600,
                                }}
                            >
                                اطلاعات کاربر
                            </div>
                            <div style={{padding: "10px 14px", fontSize: 13}}>
                                <div style={{marginBottom: 4}}>
                                    <Text type="secondary">نام و نام خانوادگی: </Text>
                                    <Text>{selectedChat.user?.fullName}</Text>
                                </div>
                                <div style={{marginBottom: 4}}>
                                    <Text type="secondary">ایمیل: </Text>
                                    <Text>{selectedChat.user?.email || "-"}</Text>
                                </div>
                                <div style={{marginBottom: 4}}>
                                    <Text type="secondary">شماره تلفن: </Text>
                                    <Text>{selectedChat.user?.phoneNumber || "-"}</Text>
                                </div>
                                <div style={{marginBottom: 4}}>
                                    <Text type="secondary">کانال: </Text>
                                    <Tag
                                        color={channelMeta(selectedChat.channel).color}
                                        style={{margin: 0}}
                                    >
                                        {channelMeta(selectedChat.channel).icon}{" "}
                                        {channelMeta(selectedChat.channel).label}
                                    </Tag>
                                </div>
                                {langLabel(selectedChat.lang) && (
                                    <div style={{marginBottom: 4}}>
                                        <Text type="secondary">زبان گفت‌وگو: </Text>
                                        <Tag
                                            color={foreignLangLabel(selectedChat.lang) ? "blue" : undefined}
                                            style={{margin: 0}}
                                        >
                                            {langLabel(selectedChat.lang)}
                                        </Tag>
                                        <Text type="secondary" style={{fontSize: 11, marginInlineStart: 6}}>
                                            با این کاربر به همین زبان صحبت کنید
                                        </Text>
                                    </div>
                                )}
                                {/* Telegram username is meaningless for a web visitor. */}
                                {selectedChat.channel !== "WEB" && (
                                    <div>
                                        <Text type="secondary">یوزرنیم تلگرام: </Text>
                                        <Text>
                                            {selectedChat.user?.telegramUsername
                                                ? `@${selectedChat.user.telegramUsername}`
                                                : "-"}
                                        </Text>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Web-visitor context: only meaningful for a web chat, so hidden entirely
                            for Telegram rather than shown as a block of dashes. */}
                        {selectedChat.channel === "WEB" &&
                            (selectedChat.pageUrl || selectedChat.referrer || selectedChat.userAgent) && (
                                <div
                                    style={{
                                        borderRadius: 12,
                                        border: `1px solid ${HAIRLINE}`,
                                        overflow: "hidden",
                                        background: SURFACE,
                                    }}
                                >
                                    <div
                                        style={{
                                            padding: "8px 12px",
                                            background: "var(--npe-surface-sunken)",
                                            borderBottom: `1px solid ${HAIRLINE}`,
                                            fontWeight: 600,
                                        }}
                                    >
                                        اطلاعات بازدیدکننده
                                    </div>
                                    <div style={{padding: "10px 14px", fontSize: 13}}>
                                        {selectedChat.pageUrl && (
                                            <div style={{marginBottom: 6}}>
                                                <Text type="secondary">
                                                    <LinkOutlined/> صفحه‌ی شروع گفت‌وگو:{" "}
                                                </Text>
                                                <a
                                                    href={selectedChat.pageUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{wordBreak: "break-all", fontSize: 12}}
                                                    dir="ltr"
                                                >
                                                    {selectedChat.pageUrl}
                                                </a>
                                            </div>
                                        )}
                                        {selectedChat.referrer && (
                                            <div style={{marginBottom: 6}}>
                                                <Text type="secondary">ارجاع‌دهنده: </Text>
                                                <Text
                                                    style={{wordBreak: "break-all", fontSize: 12}}
                                                    dir="ltr"
                                                >
                                                    {selectedChat.referrer}
                                                </Text>
                                            </div>
                                        )}
                                        {deviceFromUA(selectedChat.userAgent) && (
                                            <div>
                                                <Text type="secondary">
                                                    <DesktopOutlined/> دستگاه:{" "}
                                                </Text>
                                                <Text style={{fontSize: 12}}>
                                                    {deviceFromUA(selectedChat.userAgent)}
                                                </Text>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                        {/* The visitor's path through the site. Same WEB-only rule as the block above:
                            a Telegram chat has no pages, so the panel is absent rather than empty. */}
                        {selectedChat.channel === "WEB" && (
                            <div
                                style={{
                                    borderRadius: 12,
                                    border: `1px solid ${HAIRLINE}`,
                                    overflow: "hidden",
                                    background: SURFACE,
                                }}
                            >
                                <div
                                    style={{
                                        padding: "8px 12px",
                                        background: "var(--npe-surface-sunken)",
                                        borderBottom: `1px solid ${HAIRLINE}`,
                                        fontWeight: 600,
                                    }}
                                >
                                    مسیر بازدیدکننده در سایت
                                </div>
                                <div style={{padding: "12px 14px"}}>
                                    <VisitorJourney
                                        chatId={selectedChat.id}
                                        channel={selectedChat.channel}
                                        // Keep refreshing only while the conversation can still move.
                                        live={!isFinishedState(selectedChat.state)}
                                        compact
                                    />
                                </div>
                            </div>
                        )}

                        <div
                            style={{
                                borderRadius: 12,
                                border: `1px solid ${HAIRLINE}`,
                                overflow: "hidden",
                                background: SURFACE,
                            }}
                        >
                            <div
                                style={{
                                    padding: "8px 12px",
                                    background: "var(--npe-surface-sunken)",
                                    borderBottom: `1px solid ${HAIRLINE}`,
                                    fontWeight: 600,
                                }}
                            >
                                خلاصه چت
                            </div>
                            <div
                                style={{
                                    padding: "10px 14px",
                                    fontSize: 13,
                                    lineHeight: 1.9,
                                }}
                            >
                                {selectedChat.summary ||
                                    "خلاصه‌ای برای این چت ثبت نشده است."}
                            </div>
                        </div>
                    </div>
                </Modal>
            )}

            {selectedChat && (
                <Modal
                    open={transferModalOpen}
                    onCancel={() => {
                        setTransferModalOpen(false);
                        setTransferTargetOperatorId(null);
                    }}
                    onOk={handleConfirmTransfer}
                    okText="انتقال چت"
                    cancelText="انصراف"
                    confirmLoading={transferringChat}
                    title={
                        <Space>
                            <InfoCircleOutlined/>
                            <span>انتقال چت به اپراتور دیگر</span>
                        </Space>
                    }
                    width={420}
                >
                    <div style={{display: "flex", flexDirection: "column", gap: 12}}>
                        <Text type="secondary" style={{fontSize: 13}}>
                            لطفاً اپراتور مقصد را انتخاب کنید. این چت از لیست شما خارج شده و در لیست
                            اپراتور جدید نمایش داده می‌شود.
                        </Text>

                        <Select
                            showSearch
                            placeholder="انتخاب اپراتور مقصد"
                            value={transferTargetOperatorId || undefined}
                            onChange={(value) => setTransferTargetOperatorId(value)}
                            loading={loadingTransferOperators}
                            filterOption={(input, option) =>
                                (option?.label)
                                    .toLowerCase()
                                    .includes(input.toLowerCase())
                            }
                            options={transferOperators.map((op) => {
                                const wt = op.workSchedule?.[0];

                                if (!wt) {
                                    return {
                                        value: op.id,
                                        label: `${op.fullName} (زمان شیفت نامشخص)`,
                                    };
                                }

                                const daysFa = {
                                    SATURDAY: "شنبه",
                                    SUNDAY: "یکشنبه",
                                    MONDAY: "دوشنبه",
                                    TUESDAY: "سه‌شنبه",
                                    WEDNESDAY: "چهارشنبه",
                                    THURSDAY: "پنجشنبه",
                                    FRIDAY: "جمعه",
                                };

                                const dayText = daysFa[wt.day] ?? wt.day;

                                const formatTime = (h, m) =>
                                    `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;

                                const start = formatTime(wt.startHour, wt.startMinute);
                                const end = formatTime(wt.endHour, wt.endMinute);

                                return {
                                    value: op.id,
                                    label: `${op.fullName} (${dayText} – ${start} تا ${end})`,
                                };
                            })}
                            style={{width: "100%"}}
                        />

                        {transferOperators.length === 0 && !loadingTransferOperators && (
                            <Text type="secondary" style={{fontSize: 12}}>
                                در حال حاضر اپراتور فعالی برای شیفت بعدی پیدا نشد.
                            </Text>
                        )}
                    </div>
                </Modal>
            )}
        </div>
    );
}
